import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Modal, Button, Form, Spinner, InputGroup } from 'react-bootstrap';
import { STATE_STATUS } from '@awp/constants';

import {
  saveGraphView,
  resetGraphViewToDefault,
  getGraphViewName,
  updateGraphView,
  selectAllGraphViews,
  selectViewSaveState,
  selectViewUpdateState,
} from '@stores/graphViewPageSlice';

import PropTypes from 'prop-types';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { FormInput } from '@components/Common/AwpFormInputs';

const SaveModal = ({ isOpen, toggleModal }) => {
  const dispatch = useDispatch();
  const saveState = useSelector(selectViewSaveState);
  const updateState = useSelector(selectViewUpdateState);
  const currentGraphViewId = useSelector((state) => state.graphViewPage.graphViewId);
  const graphViewName = useSelector(getGraphViewName);
  const allGraphViews = useSelector(selectAllGraphViews);

  const [showPresetWarning, setShowPresetWarning] = useState(false);
  const [showNameExistsWarning, setShowNameExistsWarning] = useState(false);
  const [newGraphName, setNewGraphName] = useState('');

  const handleChange = (e) => {
    setNewGraphName(e.target.value);
    setShowPresetWarning(false);
    setShowNameExistsWarning(false);
  };

  const closeModal = () => {
    //reset the warnings
    if (isOpen) toggleModal();

    setShowPresetWarning(false);
    setShowNameExistsWarning(false);
  };

  const cancelSave = () => {
    if (isOpen) toggleModal();
    setShowPresetWarning(false);
    setShowNameExistsWarning(false);
  };

  useEffect(() => {
    setNewGraphName(graphViewName);
  }, [currentGraphViewId]);

  useEffect(() => {
    if (saveState == STATE_STATUS.SUCCESS || updateState == STATE_STATUS.SUCCESS) {
      closeModal();
      setShowPresetWarning(false);
      setShowNameExistsWarning(false);
    }
  }, [saveState, updateState]);

  const saveView = () => {
    let canSave = true;
    let matchedGraphId = undefined;

    if (!newGraphName || newGraphName === '') {
      canSave = false;
    }

    //if a warning is already showing then we don't need to check the warnings again?
    if (!showNameExistsWarning && !showPresetWarning) {
      if (!showNameExistsWarning && canSave) {
        //check the name
        const matchedGraphViewName = allGraphViews.find(
          (gv) => gv.Name.toLowerCase() == newGraphName.toLowerCase() && !gv.IsUnsaved && !gv.IsSystem,
        );

        if (matchedGraphViewName) {
          setShowNameExistsWarning(true);
          setShowPresetWarning(false);
          canSave = false;
        }
      }

      //if the preset warning is showing and user has clicked save,
      // they understand the warning and wish to save
      if (!showPresetWarning && canSave) {
        //user doesn't have a warning, check graph views for a system view
        const matchedSystemGraphView = allGraphViews.find(
          (gv) => gv.Name.toLowerCase() == newGraphName.toLowerCase() && gv.IsSystem,
        );

        if (matchedSystemGraphView) {
          setShowPresetWarning(true);
          setShowNameExistsWarning(false);
          canSave = false;
        }
      }
    }

    if (canSave) {
      if (showNameExistsWarning) {
        const matchedGraphViewName = allGraphViews.find(
          (gv) => gv.Name.toLowerCase() == newGraphName.toLowerCase() && !gv.IsUnsaved && !gv.IsSystem,
        );
        matchedGraphId = matchedGraphViewName?.Id;

        //save the new view
        dispatch(updateGraphView({ graphViewId: currentGraphViewId, matchedGraphViewId: matchedGraphId }));
        if (matchedGraphId) {
          //reset the original view
          dispatch(resetGraphViewToDefault({ graphViewId: currentGraphViewId }));
        }
      } else {
        //save the view
        dispatch(saveGraphView({ graphViewId: currentGraphViewId, graphName: newGraphName }));
      }
    }
  };

  const validationSchema = () => {
    const schema = Yup.object({
      view_name: Yup.string().label('View Name').max(250, '${ label } must not exceed 250 characters'),
    });

    return schema;
  };

  return (
    <Modal
      show={isOpen}
      onHide={toggleModal}
      backdrop="static"
      size="md"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header>
        <Modal.Title>New Graph View</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group style={{ marginBottom: '0px' }}>
          <Form.Label className="graphView__save__nameLabel">Please enter a new graph view name </Form.Label>
          <InputGroup hasValidation>
            <Form.Control
              name="view_name"
              id="view_name"
              type="text"
              onChange={handleChange}
              defaultValue={graphViewName}
              placeholder="Graph View"
              maxLength={250}
              isInvalid={newGraphName.length <= 0 || newGraphName.length > 250}
            />
            <Form.Control.Feedback type="invalid">Graph View must have a name.</Form.Control.Feedback>
            {showPresetWarning && (
              <div className="graphView__save__warningContainer">
                <Form.Label as="div" className="graphView__save__warningMsg">
                  This name is already in use as a preset view. It will appear with the same name in your custom view
                  list.
                </Form.Label>
              </div>
            )}
            {showNameExistsWarning && (
              <div className="graphView__save__warningContainer">
                <Form.Label as="div" className="graphView__save__warningMsg">
                  You already have a view named &quot;{newGraphName}&quot;
                </Form.Label>
                <Form.Label as="div" className="graphView__save__warningMsg">
                  Do you want to replace it with this one?
                </Form.Label>
              </div>
            )}
          </InputGroup>
        </Form.Group>
        <span style={{ display: 'flex', justifyContent: 'flex-end', fontSize: '.8rem' }}>{'(250 Characters max)'}</span>
      </Modal.Body>
      {(saveState == STATE_STATUS.IDLE || saveState != STATE_STATUS.LOADING) &&
      (updateState == STATE_STATUS.IDLE || updateState != STATE_STATUS.LOADING) ? (
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelSave}>
            {showNameExistsWarning ? 'No, Keep View' : 'Cancel'}
          </Button>
          <Button variant="primary" onClick={saveView}>
            {showNameExistsWarning ? 'Yes, Replace View' : 'Save View'}
          </Button>
        </Modal.Footer>
      ) : (
        <Modal.Footer>
          <Button variant="primary" disabled>
            <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
            Saving...
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

SaveModal.propTypes = {
  isOpen: PropTypes.bool,
  toggleModal: PropTypes.func,
};

export default SaveModal;
